import React, { useState } from "react";
import WordMorphology from "../WordMorphology";
import { tagsToLabel } from "../ParadigmSheet";

/**
 * PublicWordDetail — read-only word panel for the shared-topic page.
 *
 * Slim sibling of WordDetail.js: header + play, conjugated-form banner,
 * definition (senses + examples), plurals, and the word-structure blow-up
 * (reusing WordMorphology). No admin editing, no AI fetch, no root-family
 * fetch (that path needs an authenticated endpoint).
 */
export default function PublicWordDetail({ word, occurrence, onClear, onPlay, isPlaying }) {
  const [showStructure, setShowStructure] = useState(false);

  if (!word) return null;

  const senses = word.senses || [];
  const plurals = word.plurals || [];
  const conjugationInfo = word.conjugation_info || null;
  // Occurrence-specific structure (from the tapped token) wins over the
  // word-level one, same as WordDetail.
  const structureData = occurrence?.morphology || word.morphology || null;
  const isPlural = !!word.singular_of;

  return (
    <>
      {showStructure && structureData && (
        <WordMorphology word={structureData} onClose={() => setShowStructure(false)} />
      )}

      <div className="space-y-4 pb-6">
        {/* ── Header card ── */}
        <div className="surface-glass rounded-2xl px-4 pt-4 pb-3 space-y-1">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <h2 className="font-arabic text-3xl font-bold text-dt-gold-bright leading-tight" dir="rtl">
                {occurrence?.surfaceText || word.text}
              </h2>
              <button
                type="button"
                title={isPlaying ? "Stop" : "Play"}
                onClick={() => onPlay(word)}
                className="shrink-0 p-1.5 rounded-full text-dt-parchment-dim hover:text-dt-gold-bright hover:bg-dt-forest-soft transition-colors"
                aria-label={isPlaying ? "Stop" : "Play word"}
              >
                {isPlaying ? (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="5" y="5" width="14" height="14" rx="2" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 4l14 8-14 8V4z" />
                  </svg>
                )}
              </button>
            </div>
            {onClear && (
              <button
                onClick={onClear}
                className="shrink-0 p-2 rounded-full text-dt-parchment-dim hover:text-dt-parchment hover:bg-dt-forest-soft"
                aria-label="Close word detail"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {word.transliteration && (
            <p className="text-sm text-dt-parchment-dim italic">{word.transliteration}</p>
          )}
        </div>

        {/* ── Word structure trigger ── */}
        {structureData && (
          <button
            type="button"
            onClick={() => setShowStructure(true)}
            className="w-full flex items-center justify-center gap-2 rounded-xl py-3 font-bold text-sm transition-[filter] hover:brightness-105"
            style={{ background: "linear-gradient(90deg, var(--gold), var(--gold-bright))", color: "var(--ink)" }}
          >
            ◇ View Word Structure
          </button>
        )}

        {/* ── Conjugated-form banner (read-only, no root-family fetch) ── */}
        {conjugationInfo && (
          <div className="rounded-2xl px-4 py-3 space-y-1" style={{ background: "var(--forest-soft)", border: "1px solid var(--panel-line)" }}>
            <div className="flex items-center gap-1.5" style={{ color: "var(--gold-bright)" }}>
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-semibold">Conjugated form</p>
            </div>
            <p className="text-sm" style={{ color: "var(--parchment)" }}>
              {tagsToLabel(conjugationInfo.tags)} of{" "}
              <span className="font-arabic font-bold" style={{ color: "var(--gold-bright)" }} dir="rtl">
                {conjugationInfo.word_text}
              </span>
            </p>
            {conjugationInfo.definition && (
              <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>{conjugationInfo.definition}</p>
            )}
          </div>
        )}

        {/* ── Definition ── */}
        <section className="surface-glass rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-dt-line">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Definition</h3>
          </div>
          {senses.length === 0 && !conjugationInfo?.definition ? (
            <p className="px-4 py-4 text-sm text-dt-parchment-dim">No definition yet.</p>
          ) : (
            <div className="px-4 py-3 space-y-3">
              {senses.map((s) => (
                <div key={s.id} className="space-y-2">
                  <p className="text-sm" style={{ color: "var(--parchment)" }}>
                    {s.definition}
                    {s.base_definition && (
                      <span className="text-dt-parchment-dim"> ({s.base_definition})</span>
                    )}
                  </p>
                  {s.examples && s.examples.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Examples</p>
                      <ul className="space-y-1">
                        {s.examples.map((ex) => (
                          <li key={ex.id} className="rounded-lg bg-dt-forest-soft border border-dt-line px-3 py-2">
                            <p className="text-sm font-arabic text-dt-parchment" dir="rtl">{ex.text}</p>
                            {ex.translation && (
                              <p className="text-xs text-dt-parchment-dim mt-0.5">{ex.translation}</p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Plurals ── */}
        {!isPlural && (
          <section className="surface-glass rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-dt-line">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-dt-gold-dim">Plurals</h3>
            </div>
            <div className="px-4 py-3 space-y-1.5">
              {plurals.length === 0 ? (
                <p className="text-sm text-dt-parchment-dim">No plurals recorded.</p>
              ) : (
                plurals.map((p) => (
                  <p key={p.id} className="text-sm" style={{ color: "var(--parchment)" }}>
                    <span className="font-arabic text-base font-semibold" dir="rtl">{p.form}</span>
                    {p.transliteration && (
                      <span className="text-dt-parchment-dim italic"> — {p.transliteration}</span>
                    )}
                  </p>
                ))
              )}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
